import { ArrowLeftOutlined } from '@ant-design/icons';
import type { ProColumns } from '@ant-design/pro-components';
import { ProTable } from '@ant-design/pro-components';
import { useRequest } from 'ahooks';
import { Button, Card, Space, Tabs } from 'antd';
import { useMemo, useState } from 'react';
import { examController } from '../../api';
import { useCompany } from '../../hooks/company';
import DepartmentReport from './DepartmentReport';
import DepartmentScore from './DepartmentScore';
import ExamRank from './ExamRank';

interface ExamRow {
  examId: I.ID;
  title: string;
  startAt?: string;
  endAt?: string;
  questionCount?: number;
  totalScore?: number;
  passScore?: number;
}

// 考试成绩：进来先看到考试列表，点某场考试后按权限展示 排行榜 / 部门成绩 / 考试报表 三个 Tab。
// 进入本页至少拥有 排行榜/部门成绩/考试报表 之一权限（菜单显隐已保证），故用管理视角的考试列表接口
export default () => {
  const { id: companyId, hasPermission } = useCompany();
  const [selected, setSelected] = useState<ExamRow>();

  const canRank = hasPermission('page:exam:rank');
  const canScore = hasPermission('page:exam:score:department');
  const canReport = hasPermission('page:exam:report:department');

  // 有 rank/score/report/manage 任一即可读考试列表（后端已放宽），列出全部考试供选择
  const { data: exams, loading } = useRequest(async () => {
    const all = await examController.list(companyId, 1, 200, {});
    return (all.list || []).map((item) => ({
      examId: item.id as I.ID,
      title: item.title || '',
      startAt: item.startAt,
      endAt: item.endAt,
      questionCount: item.questionCount,
      totalScore: item.totalScore,
      passScore: item.passScore,
    }));
  });

  const tabs = useMemo(() => {
    if (!selected) {
      return [];
    }
    const items = [];
    if (canRank) {
      items.push({
        key: 'rank',
        label: '排行榜',
        children: <ExamRank examId={selected.examId} />,
      });
    }
    if (canScore) {
      items.push({
        key: 'score',
        label: '部门成绩',
        children: <DepartmentScore examId={selected.examId} />,
      });
    }
    if (canReport) {
      items.push({
        key: 'report',
        label: '考试报表',
        children: <DepartmentReport examId={selected.examId} />,
      });
    }
    return items;
  }, [selected, canRank, canScore, canReport]);

  if (selected) {
    return (
      <Card
        title={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => setSelected(undefined)}
            >
              返回考试列表
            </Button>
            <span>{selected.title}</span>
          </Space>
        }
      >
        <Tabs items={tabs} destroyInactiveTabPane />
      </Card>
    );
  }

  const columns: ProColumns<ExamRow>[] = [
    { title: '考试名称', dataIndex: 'title', ellipsis: true },
    {
      title: '考试时间',
      width: 260,
      render: (_, record) => {
        if (!record.startAt && !record.endAt) {
          return '不限';
        }
        return `${record.startAt || '不限'} ~ ${record.endAt || '不限'}`;
      },
    },
    {
      title: '题数 / 总分 / 及格线',
      width: 160,
      render: (_, record) =>
        `${record.questionCount ?? '-'} / ${record.totalScore ?? '-'} / ${record.passScore ?? '-'}`,
    },
    {
      title: '操作',
      valueType: 'option',
      width: 120,
      render: (_, record) => (
        <Button type="primary" size="small" onClick={() => setSelected(record)}>
          查看成绩
        </Button>
      ),
    },
  ];

  return (
    <ProTable<ExamRow>
      rowKey="examId"
      headerTitle="考试成绩"
      columns={columns}
      dataSource={exams}
      loading={loading}
      search={false}
      options={false}
      pagination={{ pageSize: 20 }}
    />
  );
};
